import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEmployeeContext } from '@/hooks/useEmployeeContext';
import { formatDateTime, statusBadgeClass } from '@/lib/utils';
import { getUnitAccessLogs } from '@/services/api.client';

export function EmployeeHistoryPage() {
  const ctx = useEmployeeContext();
  const [visitorName, setVisitorName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const historyQuery = useQuery({
    queryKey: [
      'employee-access-logs',
      ctx?.propertyId,
      ctx?.unitId,
      { visitorName, dateFrom, dateTo, cursor },
    ],
    queryFn: () =>
      ctx
        ? getUnitAccessLogs(ctx.propertyId, ctx.unitId, {
            ...(visitorName ? { visitorName } : {}),
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            ...(cursor ? { cursor } : {}),
            limit: 20,
          })
        : null,
    enabled: !!ctx,
  });

  const data = historyQuery.data;
  const isLoading = historyQuery.isLoading;
  const hasMore = data?.meta.hasMore ?? false;

  function resetPagination() {
    setCursor(undefined);
    setCursorStack([]);
  }

  function handleNext() {
    const next = data?.meta.nextCursor;
    if (next) {
      setCursorStack((s) => [...s, next]);
      setCursor(next);
    }
  }

  function handlePrev() {
    const stack = cursorStack.slice(0, -1);
    setCursorStack(stack);
    setCursor(stack[stack.length - 1]);
  }

  function formatDuration(minutes: number | null): string {
    if (minutes === null) return '—';
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h1 className="text-2xl">Visitor History</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Gate access events for visitors to your company.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label htmlFor="visitor-name-filter">Visitor name</Label>
          <Input
            id="visitor-name-filter"
            placeholder="Search by name…"
            value={visitorName}
            onChange={(e) => {
              setVisitorName(e.target.value);
              resetPagination();
            }}
            className="w-48"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="date-from">From</Label>
          <Input
            id="date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              resetPagination();
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="date-to">To</Label>
          <Input
            id="date-to"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              resetPagination();
            }}
          />
        </div>
      </div>

      {historyQuery.isError ? (
        <div
          role="alert"
          className="rounded-xl border border-[var(--border-error)] bg-[var(--status-red-tint)] p-4"
        >
          <p className="text-sm font-medium text-[var(--text-primary)]">
            Couldn&apos;t load visitor history
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            The history list is unavailable. Please try again.
          </p>
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Visitor</TableHead>
              <TableHead>Gate</TableHead>
              <TableHead>Check-in</TableHead>
              <TableHead>Check-out</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.data.length ? (
              data.data.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{entry.visitorName}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{entry.gateName}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(entry.checkedInAt)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {entry.checkedOutAt ? formatDateTime(entry.checkedOutAt) : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDuration(entry.durationMinutes)}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusBadgeClass(entry.status)}>{entry.status}</Badge>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No gate access events found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrev}
          disabled={cursorStack.length === 0}
        >
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={handleNext} disabled={!hasMore}>
          Next
        </Button>
      </div>
    </div>
  );
}
